import * as React from "react";
import {
    autoUpdate,
    flip,
    offset,
    safePolygon,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import { PopoverOptions } from "@/common/Popover/index.tsx";

export function usePopover({
    initialOpen = false,
    placement = "bottom",
    modal,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    autoUpdatePosition = true,
    interactionKind,
}: PopoverOptions = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
    const [labelId, setLabelId] = React.useState<string | undefined>();
    const [descriptionId, setDescriptionId] = React.useState<
        string | undefined
    >();

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdatePosition ? autoUpdate : undefined,
        middleware: [
            offset(5),
            size({
                apply({ availableWidth, availableHeight, elements }) {
                    // Change styles, e.g.
                    Object.assign(elements.floating.style, {
                        maxWidth: `${Math.max(0, availableWidth)}px`,
                        maxHeight: `${Math.max(0, availableHeight)}px`,
                    });
                },
            }),
            flip({
                crossAxis: placement.includes("-"),
                fallbackAxisSideDirection: "end",
                padding: 5,
            }),
            shift({ padding: 5 }),
        ],
    });

    const context = data.context;

    const interactionItems = [];

    // Interaction kind should never change so it's safe to call conditionally
    if (interactionKind === "hover") {
        // eslint-disable-next-line @eslint-react/rules-of-hooks
        const hover = useHover(context, { handleClose: safePolygon() });
        interactionItems.push(hover);
    } else {
        // eslint-disable-next-line @eslint-react/rules-of-hooks
        const click = useClick(context, {
            enabled: controlledOpen == null,
        });
        interactionItems.push(click);
    }

    const dismiss = useDismiss(context, {
        outsidePress: (event) => {
            // Don't dismiss when clicking inside a Headless UI portal (e.g. Listbox options)
            const target = event.target as Element | null;
            if (target?.closest?.("[data-headlessui-portal]")) {
                return false;
            }
            return true;
        },
    });
    const role = useRole(context);
    interactionItems.push(dismiss);
    interactionItems.push(role);

    const interactions = useInteractions(interactionItems);

    return React.useMemo(
        () => ({
            open,
            setOpen,
            ...interactions,
            ...data,
            modal,
            labelId,
            descriptionId,
            setLabelId,
            setDescriptionId,
        }),
        [open, setOpen, interactions, data, modal, labelId, descriptionId],
    );
}
type ContextType =
    | (ReturnType<typeof usePopover> & {
          setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
          setDescriptionId: React.Dispatch<
              React.SetStateAction<string | undefined>
          >;
      })
    | null;
export const PopoverContext = React.createContext<ContextType>(null);
export const usePopoverContext = () => {
    const context = React.useContext(PopoverContext);

    if (context == null) {
        throw new Error("Popover components must be wrapped in <Popover />");
    }

    return context;
};
